/**
 * useSelectionCapture — React hook that listens for text selection events on a
 * given container and calls back with the NFC-normalised character offsets
 * whenever a valid selection falls entirely within a single [data-block-id]
 * element.
 *
 * Uses the same selection logic as bridgeJs.ts (ported to TypeScript in
 * packages/reader-render/src/highlightDom.ts) so offset semantics are
 * identical between capture (here) and paint (useHighlightPainter).
 *
 * Consumed by:
 *   - apps/platform Reader.tsx (web)
 *   - apps/desktop (future)
 */

import { useEffect, useRef } from 'react'
import { selectedRangeInBlock } from '@arianna/reader-render'
import type { SelectedRange } from '@arianna/reader-render'

export type { SelectedRange }

export interface SelectionCaptureOptions {
  /** The container element to scope events to. Pass a ref.current. */
  container: HTMLElement | null
  /** Called when a valid selection is captured (debounced ~80 ms). */
  onSelection: (range: SelectedRange) => void
  /** Called when the selection is cleared or collapses. */
  onCleared: () => void
  /** Debounce delay in ms. Default: 80. */
  debounceMs?: number
}

/**
 * Attaches selectionchange + mouseup + touchend listeners to detect when the
 * user selects text within a [data-block-id] element inside `container`.
 *
 * All listeners are removed on unmount or when `container` changes.
 */
export function useSelectionCapture({
  container,
  onSelection,
  onCleared,
  debounceMs = 80,
}: SelectionCaptureOptions): void {
  // Use refs for the callbacks so we don't need to include them in the
  // useEffect dependency array — avoids re-attaching listeners every render.
  const onSelectionRef = useRef(onSelection)
  const onClearedRef = useRef(onCleared)
  useEffect(() => {
    onSelectionRef.current = onSelection
    onClearedRef.current = onCleared
  }, [onSelection, onCleared])

  useEffect(() => {
    if (!container) return

    let timer: ReturnType<typeof setTimeout> | null = null

    function handleEvent() {
      if (timer !== null) clearTimeout(timer)
      timer = setTimeout(() => {
        timer = null
        const range = selectedRangeInBlock()
        if (range) {
          onSelectionRef.current(range)
        } else {
          onClearedRef.current()
        }
      }, debounceMs)
    }

    // selectionchange fires on the document, not the container — but we still
    // need to check the container boundary (done inside selectedRangeInBlock).
    document.addEventListener('selectionchange', handleEvent)
    // mouseup / touchend give us an immediate trigger after the gesture ends,
    // which means we don't need to wait for the browser to fire selectionchange.
    container.addEventListener('mouseup', handleEvent)
    container.addEventListener('touchend', handleEvent, { passive: true })

    return () => {
      if (timer !== null) clearTimeout(timer)
      document.removeEventListener('selectionchange', handleEvent)
      container.removeEventListener('mouseup', handleEvent)
      container.removeEventListener('touchend', handleEvent)
    }
  }, [container, debounceMs])
}
